import { HWIO_SOURCE_TYPE_NAMES, matchesSourceType } from "./Helpers";

/**
 * The single source of truth for which tag grid columns only apply to certain source types.
 *
 * Some columns surface a field that only means something for one family of source types — a Modbus
 * register, a HardWiredIO board number. On a tag of any other source type the cell shows a short
 * explanation instead of a value, cannot be opened for editing, and is skipped (never written) by
 * multi-tag operations such as paste, fill, and Bulk Update.
 *
 * To add a source-type-specific column, register it here and nothing else needs a source-type list:
 * the display message, the edit gate, the write-path rejection, and Bulk Update compatibility are
 * all derived from this map.
 *
 * Keys are column **ids** as used by the grid, `handleRowEdit`, `getCellStyle`, and the Excel
 * export — note `ModbusSlave`, not the `ModbusSlaveId` key its column definition is stored under.
 */

/** The three Modbus source types, whose InternalName and DisplayName happen to be identical. */
export const MODBUS_SOURCE_TYPE_NAMES = [
  "ModbusTCP",
  "ModbusRTU232",
  "ModbusRTU485",
];

/** TCP only — used where the TCP and RTU wire formats differ, e.g. `port` vs `port_number`. */
export const MODBUS_TCP_SOURCE_TYPE_NAMES = ["ModbusTCP"];

const MODBUS_RULE = { sourceTypes: MODBUS_SOURCE_TYPE_NAMES, label: "Modbus" };
const MODBUS_TCP_RULE = {
  sourceTypes: MODBUS_TCP_SOURCE_TYPE_NAMES,
  label: "ModbusTCP",
};
const HWIO_RULE = { sourceTypes: HWIO_SOURCE_TYPE_NAMES, label: "HardWiredIO" };

export const columnSourceTypeRules = {
  ModbusRegister: MODBUS_RULE,
  // Only TCP connections have an IP address, so this one is narrower than the rest.
  ModbusIP: MODBUS_TCP_RULE,
  ModbusPort: MODBUS_RULE,
  ModbusSlave: MODBUS_RULE,
  LoSetpoint: MODBUS_RULE,
  LoLoSetpoint: MODBUS_RULE,
  HiSetpoint: MODBUS_RULE,
  HiHiSetpoint: MODBUS_RULE,
  SyncDirection: MODBUS_RULE,
  HwioIOType: HWIO_RULE,
  HwioChannelNumber: HWIO_RULE,
  HwioBoardNumber: HWIO_RULE,
};

// Note: ModbusMapID, ModbusRegisterPLC and ModbusRegisterRTU are deliberately absent. Despite the
// names they are computed from TagIdentifier for any tag and are already non-editable.

/**
 * Returns the source-type rule for a column, or undefined when the column is unrestricted.
 * @param {string} columnId The grid column id.
 * @returns {{ sourceTypes: string[], label: string } | undefined}
 */
export const getColumnSourceTypeRule = (columnId) =>
  columnSourceTypeRules[columnId];

/**
 * Determines whether a column applies to a tag. Columns with no rule apply to every tag.
 * @param {string} columnId The grid column id.
 * @param {*} row The tag or grid row.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {boolean} True when the column is meaningful for this tag.
 */
export const isColumnCompatibleWithTag = (columnId, row, lookupValues) => {
  const rule = columnSourceTypeRules[columnId];
  if (!rule) {
    return true;
  }
  return matchesSourceType(row, rule.sourceTypes, lookupValues);
};

/**
 * The read-only explanation shown in a column's cells for tags it does not apply to.
 * @param {string} columnId The grid column id.
 * @returns {string} e.g. 'Not a Modbus Tag'; '' for an unrestricted column.
 */
export const getIncompatibleColumnMessage = (columnId) => {
  const rule = columnSourceTypeRules[columnId];
  return rule ? `Not a ${rule.label} Tag` : "";
};
